import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { PrimaryButton, GhostButton, inputCls } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { cardCls, thCls, tdCls } from "./constants";
import { Toggle, IconBtn } from "./bits";

const CUSTOM = "__custom__";

function MappingRow({ c, idx, boardCols, targets, comparable, onChange, onRemove }) {
  const isCustom = c.target.startsWith("spec:") || c.target === CUSTOM;
  return (
    <tr data-testid="column-map-row" className="border-b border-neutral-800/60 last:border-0">
      <td className={tdCls}>
        <select value={c.column_id} onChange={(e) => onChange(idx, { column_id: e.target.value, title: boardCols.find((b) => b.id === e.target.value)?.title || "" })} className={inputCls} data-testid={`column-map-board-${idx}`}>
          {!boardCols.some((b) => b.id === c.column_id) && <option value={c.column_id}>{c.title || c.column_id}</option>}
          {boardCols.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
        </select>
      </td>
      <td className={tdCls}>
        <select value={isCustom ? CUSTOM : c.target} onChange={(e) => onChange(idx, { target: e.target.value === CUSTOM ? `spec:${c.title || "Detail"}` : e.target.value })} className={inputCls} data-testid={`column-map-target-${idx}`}>
          {Object.entries(targets).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          <option value={CUSTOM}>Custom detail (shown under Additional Details)…</option>
        </select>
        {isCustom && (
          <input value={c.target.replace(/^spec:/, "")} onChange={(e) => onChange(idx, { target: `spec:${e.target.value}` })} placeholder="Label shown to customers" className={`${inputCls} mt-2`} data-testid={`column-map-label-${idx}`} />
        )}
      </td>
      <td className={`${tdCls} text-center`}>
        {comparable.includes(c.target) ? (
          <input type="checkbox" checked={!!c.compare} onChange={() => onChange(idx, { compare: !c.compare })} className="accent-[#73bf44] w-4 h-4" data-testid={`column-map-compare-${idx}`} />
        ) : <span className="text-neutral-700">—</span>}
      </td>
      <td className={`${tdCls} text-right`}>
        <IconBtn danger onClick={() => onRemove(idx)} title="Remove column" data-testid={`column-map-remove-${idx}`}><Trash size={16} /></IconBtn>
      </td>
    </tr>
  );
}

export function ColumnSettings({ onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [targets, setTargets] = useState({});
  const [comparable, setComparable] = useState([]);
  const [boardCols, setBoardCols] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/production/config").then(({ data }) => { setCfg(data.config); setTargets(data.targets); setComparable(data.comparable); })
      .catch((e) => toast.error(getErrorMessage(e)));
    adminApi.get("/admin/production/board-columns").then(({ data }) => setBoardCols(data.columns)).catch((e) => toast.error(getErrorMessage(e)));
  }, []);

  if (!cfg) return <div className="text-neutral-600">Loading…</div>;
  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const change = (idx, patch) => set({ columns: cfg.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  const remove = (idx) => set({ columns: cfg.columns.filter((_, i) => i !== idx) });
  const add = () => { const b = boardCols[0] || { id: "", title: "" }; set({ columns: [...cfg.columns, { column_id: b.id, title: b.title, target: `spec:${b.title}`, compare: false }] }); };

  const save = async () => {
    setSaving(true);
    try { const { data } = await adminApi.put("/admin/production/config", cfg); setCfg(data); toast.success("Sync settings saved"); onChanged(); }
    catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="column-settings-tab">
      <div className={`${cardCls} p-6 grid gap-5 md:grid-cols-3`}>
        <Toggle checked={cfg.auto_sync} onChange={(v) => set({ auto_sync: v })} label="Automatic daily sync" hint="Runs every day at 3:00am (Brisbane time)" testId="setting-auto-sync" />
        <Toggle checked={cfg.flag_existing} onChange={(v) => set({ flag_existing: v })} label="Flag differences on existing vans" hint="Sends mismatches to Config Discrepancies — turn off to scale back" testId="setting-flag-existing" />
        <Toggle checked={cfg.compare_rule_fields} onChange={(v) => set({ compare_rule_fields: v })} label="Also compare power & water setup" hint="Flags when the rulebook disagrees with a van's battery, solar, inverter, management, axle, plumbing or hitch" testId="setting-compare-rules" />
      </div>
      <div className={`${cardCls} overflow-x-auto`}>
        <div className="px-5 pt-5 pb-3 text-sm text-neutral-500">
          The board item name (<span className="text-[#73bf44]">Order No.</span>) is always used as the chassis number. Map any other board column to a Caravan Database field or a custom detail. Tick “Compare” to flag differences on existing vans.
        </div>
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
              <th className={thCls}>Board column</th><th className={thCls}>Caravan Database field</th><th className={`${thCls} text-center`}>Compare</th><th className={thCls}></th>
            </tr>
          </thead>
          <tbody>
            {cfg.columns.map((c, i) => <MappingRow key={i} c={c} idx={i} boardCols={boardCols} targets={targets} comparable={comparable} onChange={change} onRemove={remove} />)}
          </tbody>
        </table>
        <div className="flex justify-between gap-2 p-5 border-t border-neutral-800">
          <GhostButton onClick={add} data-testid="column-map-add"><Plus size={15} /> Add column</GhostButton>
          <PrimaryButton onClick={save} disabled={saving} data-testid="sync-settings-save"><FloppyDisk size={15} /> {saving ? "Saving…" : "Save settings"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}
